import type { ErrorObject } from 'serialize-error'

import type { StringWithAutocomplete } from '@/types/helpers'

/*
 * Enums
 */
export enum DeliveryMode {
  Quote = 0,
  Adaptation = 1,
  Paraphrase = 2,
}

export enum ContentModuleStatus {
  Published = 'published',
  Draft = 'draft',
  Removed = 'removed',
  PendingApproval = 'pending_approval',
  Testing = 'testing',
}

/*
 * Common Types
 */
export type SafeRequestResponse<ResponseT, ErrorDataT = { message?: string }> =
  | { ok: false; data: ErrorDataT; error: ErrorObject | undefined; isNetworkError: boolean; statusCode?: number }
  | { ok: true; data: ResponseT }

export type AIGenerateTemplateEntry = {
  status: 'generating' | 'done' | 'failed'
  result: string | null
  error: string | null
  updatedAt: string
}

export type ConfigResponse = ImportMeta['env']

type StepOrTaskIdParams<T extends string = 'task_id' | 'step_id'> = T extends 'task_id'
  ? { task_id: string; step_id?: never }
  : { step_id: string; task_id?: never }

type StepOrTaskOrResourceIdParams<T extends string = 'task_id' | 'step_id' | 'resource_id'> = T extends 'task_id'
  ? { task_id: string; step_id?: never; resource_id?: never }
  : T extends 'step_id'
    ? { step_id: string; task_id?: never; resource_id?: never }
    : { resource_id: string; task_id?: never; step_id?: never }

/*
 * Request Types
 */
export type LoginParams = { username: string; password: string }

export type ForgotParams = { username: string; old_password: string; new_password: string }

export type ForgotPasswordParams = { email: string }

export type ConfirmForgotPasswordParams = {
  email: string
  code: string
  newPassword: string
}

export type LogoutParams = { access_token: string; refresh_token: string }

export type RefreshTokenParams = { refresh_token: string }

export type NotifyLoginParams = {}

export type CreateContentModuleParams =
  { content: string | null } | { file: File } | { file_name: string; s3_file: string }

export type UploadParams = { file_path: string; file_content: File }

export type DownloadParams = { file_path: string }

export type DownloadRenderedParams = {
  file_id: string
}

export type DownloadRenderedResponse = string

export type PrepareUploadParams = { fileType: string }

export type GetContentModuleParams = { task_id: string }

export type GetContentParams = StepOrTaskIdParams & {
  file_id?: string
}

export type GetResourcesParams = StepOrTaskIdParams

export type GetFAQsParams = StepOrTaskIdParams

export type GetContentModulesParams = {
  exclude_removed?: boolean
}

export type UpdateContentModuleParams = {
  task_id: string
  /**
   * When `false`, sent as `?lock=false` and the backend bypasses the edit-lock
   * check. Use for table-level updates (topic, priority, etc.) made outside an
   * active edit session. Defaults to `true` (lock is enforced) when omitted.
   * Not sent in the request body.
   */
  lock?: boolean
  priority?: number
  name?: string
  title?: string
  short_description?: string
  long_description?: string
  are_step_sequential?: boolean
  select_esk?: string
  categories?: string[]
  mappings?: string[]
  s3_file?: string | null // UUID
  file_name?: string | null
  demographic?: string | null
  visible_to_msk?: boolean
  allowed_for_all_sponsors?: boolean
  title_image?: string | null
  introduction?: string | null
  delivery_mode?: number | null // Delivery mode setting (deprecated)
  section_objectives?: string | null // Section objectives (deprecated, but available when visibleToMsk is true)
  outline?: string | null // Generated backend-side and no longer sent by the portal (C2-2647)
  guidance?: string | null // The expert's own notes for the Sidekick; never generated (C2-2649)
  presentation_personality?: string | null // Customize Delivery section
  presentation_objectives?: string | null // Customize Delivery section
  presentation_style?: string | null // The chosen style's prose
  presentation_style_name?: string | null // The chosen style's name; the backend derives is_media_mode from it
  // Rejected and nulled by the backend if the step is not part of this module.
  primary_step_id?: string | null
  is_media_mode?: boolean | null
  /**
   * Media-mode channels only. When true, restricts the CM to subscribers; when
   * false (the default for null), the CM is available to free/guest users.
   * Only updatable by sponsor admins, typically from the CM table (lock=false).
   */
  subscription_only?: boolean | null
}

export type CreateFAQsParams = {
  faqs: Array<{
    question: string
    answer: string
  }>
  task_id?: string
  step_id?: string
}

export type UpdateFAQsParams = {
  faqs: Array<{
    faq_id: string
    question: string
    answer: string
  }>
  task_id?: string
  step_id?: string
}

export type DeleteFAQsParams = {
  faq_ids: string | string[] // Can be a single string or array of strings
  task_id?: string
  step_id?: string
}

// `speaker_names_metadata` is a JSON-encoded SpeakerNamesMetadata; only overwritten when sent, else untouched.
export type UpdateContentTextParams = StepOrTaskIdParams &
  (
    | { text: string; speaker_names_metadata?: string }
    | { file: File }
    | { file_name: string; s3_file: string }
    | { url: string; use_llm_for_scraping?: boolean }
  ) &
  ProcessProgressParams

export type CreateResourceParams = ProcessProgressParams & {
  file_name?: string | null
  file?: File
  s3_file?: string | null // UUID
  type: ResourceType
  title: string
  description: string
  data?: string
  keywords: string[]
  task_id?: string
  step_id?: string
  is_approved?: boolean
  show_in_app?: boolean
}

/**
 * Every field is a partial update — `/cm/update_resource` keeps the stored value for
 * anything omitted — so a caller changing one thing sends only that thing. Widening a
 * payload beyond what changed is how a field gets clobbered by a stale local copy.
 */
export type UpdateResourceParams = Partial<CreateResourceParams> & {
  resource_id: string
  // `''` requests removal (axios formSerializer drops `null`).
  thumbnail?: string
}

export type DeleteResourceParams = {
  resource_id: string
  task_id?: string
  step_id?: string
}

export type GetStepsParams = { task_id: string }

// FIXME: this is a hack so we can put onProgress functions into mutations, no other idea how to have a query fn communicate
// with a parent component
export type ProcessProgressParams = { onProgress?: (progress: ProcessProgress['processProgress']) => void }

export type CreateStepParams = {
  task_id: string
  preceding_step_id?: string
  // Option 1: Content as string
  content?: string
  // Option 2: Direct File upload
  file?: File
  // Option 3: S3 file reference (often used with file option after upload)
  file_name?: string
  s3_file?: string
  // Option 4: URL extraction
  url?: string
  use_llm_for_scraping?: boolean
} & ProcessProgressParams

export type DeleteStepParams = {
  step_id: string
  task_id: string
  showToast?: boolean
}

export type UpdateStepParams = {
  step_id: string
  name?: string
  title_image?: string
  description?: string
  introduction?: string
  delivery_mode?: DeliveryMode
  section_objectives?: string
  use_in_rag?: boolean
}

// Per-speaker identity keyed by transcript label. `accepted` (expert-confirmed)
// wins over `suggested` (AI guess) for display; both null means unnamed.
export type SpeakerNamesMetadata = Record<string, { suggested: string | null; accepted: string | null }>

// Fresh AI guesses only; callers merge into local `suggested`, never `accepted`.
export type SuggestSpeakerNamesParams = {
  step_id?: string
  transcript?: string
  hint?: string
}

export type SuggestSpeakerNamesResponse = {
  speakerNames: Record<string, string>
}

// Preview only: returns the rewritten transcript + metadata, persists neither.
export type ApplySpeakerNamesParams = {
  step_id?: string
  transcript?: string
  speaker_names?: Record<string, string>
}

export type ApplySpeakerNamesResponse = {
  transcript: string
  speakerNamesMetadata: SpeakerNamesMetadata
}

export type ReorderStepsParams = {
  task_id: string
  step_ids: string[]
}

export type ChangeContentModuleStatusParams = {
  task_id: string
  status: `${ContentModuleStatus}`
}

export type CopyContentModuleParams = {
  task_id: string
}

export type GenerateMetadataParams = StepOrTaskOrResourceIdParams & {
  number_of_options?: number
  field?:
    'name' | 'title' | 'short_description' | 'long_description' | 'introduction' | 'description' | 'section_objectives'
  /** Data passed as placeholders to the generation prompts */
  extra_data?: {
    /** Name of the field being edited */
    field_name?: string
    /** Last value of the field saved, accepted, or manually entered (white background text in the portal) */
    accepted_data?: string
    /** Last value of the field proposed by AI, but not yet accepted by the user (blue background text in the portal) */
    proposed_data?: string
    /** Guidance from the user on how the field generation should change */
    user_instructions?: string
  }
}

export type GenerateDemographicParams = {
  task_id: string
  /** Data passed as placeholders to the generation prompts */
  extra_data?: {
    /** Name of the field being edited */
    field_name?: string
    /** Last value of the field saved, accepted, or manually entered (white background text in the portal) */
    accepted_data?: string
    /** Last value of the field proposed by AI, but not yet accepted by the user (blue background text in the portal) */
    proposed_data?: string
    /** Guidance from the user on how the field generation should change */
    user_instructions?: string
  }
}

export type GenerateObjectivesParams = {
  task_id: string
  /** Data passed as placeholders to the generation prompts */
  extra_data?: {
    /** Name of the field being edited */
    field_name?: string
    /** Last value of the field saved, accepted, or manually entered (white background text in the portal) */
    accepted_data?: string
    /** Last value of the field proposed by AI, but not yet accepted by the user (blue background text in the portal) */
    proposed_data?: string
    /** Guidance from the user on how the field generation should change */
    user_instructions?: string
  }
}

export type GenerateFAQsParams = StepOrTaskIdParams

// Video metadata generation using LLM vision capabilities
export type GenerateVideoMetadataParams = {
  resource_id: string
  video_title?: string
  video_description?: string
}

// Image metadata generation using LLM vision capabilities
export type GenerateImageMetadataParams = {
  resource_id: string
  image_title?: string
  image_description?: string
}

/*
 * Response Types
 */
export type LoginResponse = {
  status?: number
  message: string
  authenticationResult?: {
    accessToken: string
    expiresIn: number
    tokenType: string
    refreshToken: string
    idToken: string
  }
  sponsorId?: string
  environmentName?: string
  targetUrls?: {
    smeServerUrl: string
    webServerUrl: string
  }
  requiresSponsorSelection?: boolean
  availableSponsors?: {
    sponsorId: string
    environmentName: string
    targetUrls: {
      smeServerUrl: string
      webServerUrl: string
    }
  }[]
}

export type LoginResponseError = {
  message: string
  code: 'NOT_AUTHORIZED' | 'NEW_PASSWORD_REQUIRED' | 'NO_PORTAL_ACCESS'
}

export type ForgotResponse = {
  message: string
}

export type ForgotResponseError = {
  code?: 'OLD_PASSWORD_INCORRECT'
  message: string
}

export type ForgotPasswordResponse = {
  detail: string
}

export type ConfirmForgotPasswordResponse = {
  detail: string
}

export type ConfirmForgotPasswordResult =
  { kind: 'ok'; message: string } | { kind: 'bad-data'; data?: any } | { kind: 'unknown'; error?: any; data?: any }

export type ForgotPasswordResponseError = {
  message: string
}

export type NotifyLoginResponse = {
  result: string
}

export type PrepareUploadResponse = {
  // Signed URL fragment (`<uuid>?Policy=…&Signature=…`), not a bare key — strip query string for write-side params.
  filePath: string
  fileName: string
}

export type CreateContentModuleResponse = {
  taskId: string
  inProcess: boolean
  generated?: {
    name: string
    title: string
    longDescription: string
    shortDescription: string
  }
}

export type UpdateContentModuleResponse = {}

export type UpdateContentTextResponse = {
  message: string
  inProcess: boolean
  stepRepublishing?: boolean
}

export type BaseSidekickResponse = {
  name: string
  ttsId: string
  createdBy: string
  sponsorId: string
  active: boolean
  createdAt: string
  avatarId: string
  avatarImage: string
  avatarRiveFile: string
  pitch: number
  voiceSpeed: number
  gender: string
}

export type ESKResponse = BaseSidekickResponse & {
  type: string
  introduction: string
  systemPrompt: string
  allowedForSwitchCm: boolean
  topicId: string | null
  additionalInformation: string
  category: string
  expertise: string
  notRemovable: boolean
}

export type ESKRequest = {
  type: string
  name: string
  introduction: string
  system_prompt: string
  allowed_for_switch_cm: boolean
  tts_id: string
  topic_id: string
  active: boolean
  created_at: string
  additional_information: string
  avatar_id: string
  category: string | null
  created_by: string
  expertise: string
  gender: string
  avatar_image: string
  avatar_rive_file: string
  voice_speed: number
  pitch: number
  not_removable: boolean
  sponsor_id: string
}

export type MSKResponse = BaseSidekickResponse & {
  id?: string
  _id?: string
  default: boolean
  isRequired: boolean
}

export type MSKRequest = {
  id?: string
  _id?: string
  default: boolean
  name: string
  tts_id: string
  created_by: string
  sponsor_id: string
  active: boolean
  created_at: string
  avatar_id: string
  avatar_image: string
  avatar_rive_file: string
  pitch: number
  voice_speed: number
  gender: string
  isRequired: boolean
}

export type GetESKsResponse = Array<ESKResponse & { eskId: string }>

export type GetSidekicksResponse = {
  mskTypes: Array<MSKResponse>
  eskTypes: Array<ESKResponse>
}

export type AdditionalOwner = {
  userId: string
  hotEdit?: boolean
  directPublish?: boolean
  email?: string
  name?: string
}

/**
 * Per-field AI provenance. `edited` means a person wrote the current value, so the
 * publish-time regeneration leaves it alone; `generated` means it is the machine's
 * and still refreshable. Accepting an AI suggestion stays `generated` on purpose.
 *
 * A field with no entry has not been written since the backend started recording.
 */
export type FieldProvenance = {
  source: 'generated' | 'edited'
  updatedAt: string | null
  updatedBy: string | null
}

/** Only the fields the publish-time regeneration rewrites carry provenance. */
export type ContentModuleFieldSources = Partial<
  Record<
    'longDescription' | 'subscriptionBenefits' | 'demographic' | 'introduction' | 'outline' | 'presentationStyle',
    FieldProvenance
  >
>

/** File processing writes both of these; `description` reaches the form as the file description. */
export type StepFieldSources = Partial<Record<'name' | 'description', FieldProvenance>>

export type GetContentModuleResponse = {
  approvable: boolean
  access: unknown // NOT IMPLEMENTED
  /**
   * Which file the expert designated as primary. Portal-owned since C2-2596. Absent, not
   * null, on a module saved before it existed — `to_dict` drops empty values.
   */
  primaryStepId?: string | null
  areStepSequential: boolean | null
  categories: string[] | null // List of topic names (deprecated)
  topics: string[] | null // List of topic IDs
  createdAt: string | null
  createdBy: string | null
  owner: string | null
  additionalOwners?: AdditionalOwner[] | null
  editable: boolean
  mappings: string[] | null
  name: string | null
  selectEsk: string | null
  introduction: string | null
  shortDescription: string | null
  status: `${ContentModuleStatus}` | null
  taskId: string | null
  title: string | null
  titleImage: string | null
  inProcess: boolean | null
  faqs: GetFAQsResponse | null
  demographic: string | null
  priority: number | null
  visibleToMsk: boolean | null
  allowedForAllSponsors: boolean | null
  deliveryMode: number | null // Delivery mode setting (deprecated)
  sectionObjectives: string | null // Section objectives (deprecated, but available when visibleToMsk is true)
  outline: string | null // Generated backend-side and never shown in the portal (C2-2647)
  guidance: string | null // The expert's own notes for the Sidekick; never generated (C2-2649)
  presentationPersonality: string | null // Customize Delivery section
  presentationObjectives: string | null // Customize Delivery section
  presentationStyle: string | null // The chosen style's prose, still portal-written
  /**
   * Which delivery mode the expert picked, by the style's `name` (C2-2598). The backend
   * resolves it case-insensitively, normalises it, and derives `is_media_mode` from it,
   * so the portal stops sending that flag once a choice is recorded.
   *
   * Null on a module saved before the picker existed — decision 16's Custom case.
   */
  presentationStyleName?: string | null
  isMediaMode: boolean | null
  /**
   * Media-mode channels only. `true` = subscriber-only access, `false`/null =
   * available to free/guest users. Drives the "Media Access" column on the CM
   * table; field is read-only on non-media channels.
   */
  subscriptionOnly: boolean | null
  error?: {
    message: string
    hasError: boolean
  } | null
  transcodeCompleted?: boolean
  transcodeSuccess?: boolean
  aiGenerateTemplates?: Record<string, AIGenerateTemplateEntry> | null
  fieldSources?: ContentModuleFieldSources | null
  lockedBy?: { userId: string; email: string; name: string } | null
}

export type CopyContentModuleResponse = GetContentModuleResponse

export type UpdateAdditionalOwnersParams = {
  task_id: string
  additional_owners: Array<{
    user_id: string
    hot_edit?: boolean
    direct_publish?: boolean
    email?: string
    name?: string
  }>
}

export type UpdateAdditionalOwnersResponse = GetContentModuleResponse

export type LockContentModuleParams = { task_id: string }

export type UnlockContentModuleParams = { task_id: string; force_unlock?: boolean }

export type GetContentModulesResponse = GetContentModuleResponse[]

export type ProcessProgress = {
  processProgress?: {
    current: number
    total: number
    unit: string
    error?: string | { message: string }
  } | null
}

export type GetContentResponse = ProcessProgress & {
  contentId: string
  stepId: string | null
  taskId: string | null
  inProcess: boolean
  content: string
  contentKey: string | null
  sourceUrl: string | null
  speakerNamesMetadata?: SpeakerNamesMetadata | null
  transcodeCompleted?: boolean
  transcodeSuccess?: boolean
}

export type GetFAQsResponse = Array<{
  faqId: string
  createdAt: string
  question: string
  answer: string
}>

export type ResourceType =
  'url' | 'youtube' | 'image' | 'video' | 'text' | 'pdf' | 'pptx' | 'docx' | 'audio' | 'csv' | 'xlsx'

// Types with no in-app viewer: extracted content feeds RAG, file is never shown.
export const INFORMATION_ONLY_RESOURCE_TYPES: readonly ResourceType[] = ['text', 'csv', 'xlsx']

export const isInformationOnlyResourceType = (type: ResourceType | undefined | null): boolean =>
  type != null && INFORMATION_ONLY_RESOURCE_TYPES.includes(type)

// Formats the expert thinks of as media rather than as a file. Drives the wording of
// the file modal (SME-1098 decision 10) and the module's derived media framing.
export const MEDIA_RESOURCE_TYPES: readonly ResourceType[] = ['video', 'audio', 'youtube', 'image']

export const isMediaResourceType = (type: ResourceType | undefined | null): boolean =>
  type != null && MEDIA_RESOURCE_TYPES.includes(type)

export type GetResourcesResponse = Array<
  ProcessProgress & {
    resourceId: string
    type: ResourceType
    fileName: string | null
    data: string
    title: string
    description: string
    keywords: string[] | null
    s3File?: string
    inProcess?: boolean
    resourceKey?: string
    isApproved?: boolean

    updatedAt: string
    updatedBy: string
    sourceUrl: string | null
    showInApp: boolean
    showInAppSupported: boolean
    isSourceFile?: boolean
    // Signed URL fragment, append to `${serverBaseUrl}/files/` to display.
    thumbnailS3Key?: string | null
    // Set by the backend only for `youtube` resources.
    youtubeId?: string | null
  }
>

export type GetStepsContentParams = { task_id: string }

/**
 * Steps come back in the module's step order with the same ids. `content` is null
 * and `resources` empty for a step with no content document yet; an unknown
 * `task_id` returns an empty list rather than an error.
 *
 * Signed file URLs are regenerated per response, so compare keys, never URLs.
 */
export type GetStepsContentResponse = {
  taskId: string
  steps: Array<{
    stepId: string
    content: GetContentResponse | null
    resources: GetResourcesResponse
  }>
}

export type GetStepsResponse = Array<{
  stepId: string
  name: string | null
  titleImage: string | null
  sectionObjectives: string | null
  description: string | null
  introduction: string | null
  createdAt: string
  deliveryMode: DeliveryMode | null
  faqs: GetFAQsResponse | null

  // Shadow copies of what processing generated. `name`/`description` hold the live
  // values; comparing the two is how the AI tag knows the expert hasn't edited them.
  titleAi?: string | null
  descriptionAi?: string | null
  fieldSources?: StepFieldSources | null

  storeAsSingleChunk: boolean
  useInRag?: boolean
  inProcess?: boolean | null
  // Result of the last hot-edit publish or de-index, run after the response. Mirrors
  // the module's `error`.
  error?: {
    message: string
    hasError: boolean
  } | null
  aiGenerateTemplates?: Record<string, AIGenerateTemplateEntry> | null
}>

export type StepRepublishingResponse = {
  message?: string
  stepRepublishing?: boolean
}

export type GetStepParams = { step_id: string }
export type GetStepResponse = GetStepsResponse[number]

export type CreateStepResponse = {
  stepId: string
  inProcess: boolean
  stepRepublishing?: boolean
  generated?: {
    introduction: string
    name: string
    overview: string
    shortDescription: string
    title: string
  }
}

export type UpdateStepResponse = StepRepublishingResponse

export type ChangeContentModuleStatusResponse =
  | {
      status: boolean
      message?: string
    }
  | {
      errors: Array<{
        error: string
        field: string
      }>
    }

export type GenerateMetadataResponse = {
  generated: [
    {
      name: string
      title: string
      description: string
      shortDescription: string
      longDescription: string
      introduction: string
    },
  ]
}

export type GenerateFAQsResponse = {
  questions: Array<{
    question: string
    answer: string
  }>
}

// Response from video metadata generation
export type GenerateVideoMetadataResponse = {
  title: string
  description: string
}

// Response from image metadata generation
export type GenerateImageMetadataResponse = {
  title: string
  description: string
}

export type GenerateDemographicResponse = {
  demographic: string
}

export type GenerateObjectivesResponse = {
  objectives: string
}

export type GenerateParams = (StepOrTaskIdParams | { content_id: string }) & {
  system_prompt_template_id: string
  message_template_id: string
  extra_data?: {
    accepted_data?: string
    proposed_data?: string
    user_instructions?: string
    previous_value?: string
    faq_list?: string
    [key: string]: string | undefined
  }
}

export type GenerateResponse = string

export type CreateResourceResponse = {
  resourceId: string
  inProcess: boolean
}

export type UpdateResourceResponse = {
  message: string
  inProcess: boolean
}

/*
 * Setup Progress Types
 */
export interface SetupProgress {
  createContent: boolean
  inviteUsers: boolean
}

export const getDefaultSetupProgress = (): SetupProgress => ({
  createContent: false,
  inviteUsers: false,
})

export const isSetupProgressEmpty = (progress: SetupProgress): boolean => {
  return !progress.createContent && !progress.inviteUsers
}

export type CheckUserResponse = {
  access: {
    sme: boolean
    app: boolean
  }
  isSponsor: boolean
  isSponsorAdmin?: boolean
  isSwiAdmin?: boolean
  isEditor: boolean
  sponsorId: string
  sponsorName: string
  userId: string
  email: string

  // HACK: Below are additional fields that are the response of the API on stag and internal.
  message?: StringWithAutocomplete<'success'>
}

export type SponsorListItem = {
  sponsorId: string
  sponsorName: string
  environmentName: string
  environmentUrl: string
}

export type GetSponsorListResponse = {
  sponsors: Array<SponsorListItem>
}

export type DeleteSponsorResponse = {
  message?: string
}

export type GetCurrentSponsorPlaceholdersResponse = {
  sponsorCrisisInstruction: string
  sponsorNameShort: string
  sponsorName: string
  userTypeName: string
  smeIntroduction?: string
  memberIntroduction?: string
  sponsorAdminIntroduction?: string
}

export type GetCustomerTerminologyPlaceholderResponse = GetCurrentSponsorPlaceholdersResponse & {
  expertSidekick: string
  faqTerm: string
  frequentlyAskedQuestion: string
  memberSidekickType: string
  roadToSuccess: string
  roadToSuccessItem: string
  roadToSuccessShort: string
  smeTerm: string
  step: string
  sponsorSpecificPrompt: string
  subjectMatterExpert: string
}

export type UpdateTerminologyParams = GetCustomerTerminologyPlaceholderResponse

/**
 * Request params for updating channel brand fields via /sponsor/update.
 * Uses snake_case to match the backend API contract.
 *
 * `brand_color` is included optimistically -- the backend field is pending;
 * it will be a no-op until the backend adds support.
 */
export type UpdateChannelBrandParams = {
  brand_image?: string | null
  brand_title?: string
  brand_subtitle?: string
  brand_color?: string
}

export type SaveBrandingParams = {
  brand: UpdateChannelBrandParams
  terminology: Partial<UpdateTerminologyParams>
}

export type TopicResponse = {
  id: string
  key: string
  displayName: string
  subtopics: Array<{ id: string; key: string; displayName: string }>
  priority: number | null
  skId: string | null
  notRemovable?: boolean
}

export type TopicRequest = {
  id: string
  key: string
  displayName: string
  subtopics: Array<{ id: string; key: string; displayName: string }>
  priority: number | null
  sk_id: string | null
  not_removable?: boolean
}

export type GetSponsorTopicsResponse = Array<TopicResponse>

export type UpdateTopicPriorityParams = {
  topics: Array<{
    id: string
    displayName: string
    key: string
    priority: number
    subtopics: Array<{ id: string; displayName: string; key: string }>
    skId?: string
    notRemovable?: boolean
  }>
}

export type UpdateEskParams = {
  esks: Array<ESKResponse>
  topics: Array<TopicResponse>
}

export type UpdateEskResponse = {
  message: string
  inProcess: boolean
}

export type UpdateMskParams = {
  msks: Array<MSKResponse>
}

export type UpdateMskResponse = {
  message: string
  inProcess: boolean
}
/*
 * Analytics
 */

// Analytics Request Params
// ------------------------------------------------------------
export type DateRangeRequest = {
  /**
   * What date to filter the results from. Can either be a date 2021-01-01,
   * or a relative date, like -7d for last seven days, -1m for last month,
   * mStart for start of the month or yStart for the start of the year.
   *
   */
  date_from: string
  /**
   * What date to filter the results to. Can either be a date 2021-01-01,
   * or a relative date, like -7d for last seven days, -1m for last month,
   * mStart for start of the month or yStart for the start of the year.
   */
  date_to: string | null
  explicit_date: boolean
}

export type GetCmConversationsByDateParams = DateRangeRequest & {
  cm_id: string
}

export type GetTopicConversationsByDateParams = DateRangeRequest & {
  topic_id: string
}

export type GetActiveMembersByDateParams = DateRangeRequest

export type GetSmeLoginsByDateParams = DateRangeRequest

export type AllConversationsPerCmParams = DateRangeRequest

export type GetEditsPerCmParams = DateRangeRequest

export type GetCmEditsByDateParams = DateRangeRequest

// Analytics Response Types
// ------------------------------------------------------------
export type ActiveMembersByDateResponse = DateRange & {
  data: ActiveMembersByDateEntry[]
  stats: AnalyticsStats
}

export type SmeLoginsByDateResponse = DateRange & {
  data: SmeLoginsByDateEntry[]
  stats: AnalyticsStats
}

export type CmConversationByDateResponse = DateRange & {
  data: CmConversationByDateEntry[]
  stats: ConversationStats
}

export type TopicConversationByDateResponse = DateRange & {
  data: TopicConversationByDateEntry[]
  stats: ConversationStats
}

export type AllConversationsPerCmResponse = DateRange & {
  data: AllConversationsPerCmEntry[]
  stats: ConversationStats
}

export type EditsPerCmResponse = DateRange & {
  data: EditsPerCmEntry[]
  stats: AnalyticsStats
}

export type CmEditsByDateResponse = DateRange & {
  data: CmEditsByDateEntry[]
  stats: AnalyticsStats
}

// Analytics Response Entry Types
// ------------------------------------------------------------
/**
 * The start and end dates of the data returned from the analytics API.
 * These are the dates of the data returned, not necessarily the date range of the request.
 * Will be null if no data is returned. Dates are in the format YYYY-MM-DD.
 */
export type DateRange = {
  startDate: string | null
  endDate: string | null
}

export interface ContentModuleType {
  label: string
  taskId: string
}

export type BaseConversationEntry = {
  total: number // total number of conversations (unique + non-unique)
  unique: number // total number of unique user conversations
}

export type ActiveMembersByDateEntry = {
  date: string
  total: number
}

export type SmeLoginsByDateEntry = {
  date: string
  total: number
}

export type CmEditsByDateEntry = {
  date: string
  total: number
}

// Type for each entry in the histogram data
export type AllConversationsPerCmEntry = ContentModuleType &
  BaseConversationEntry & {
    total_messages: number // total number of messages in all conversations for the Content Module
  }

export type EditsPerCmEntry = ContentModuleType & {
  total: number
  unique: number
}

export type CmConversationByDateEntry = {
  date: string // YYYY-MM-DD
  total: number // total number of conversations on that date for the specific CM
  unique: number // total number of unique user conversations on that date for the specific CM
  total_messages: number // total number of messages in all conversations on that date for the specific CM
  all_conversations: number // total number of conversations on that date for all CM
}

export type TopicConversationByDateEntry = {
  date: string // YYYY-MM-DD
  total: number // total number of conversations on that date for the specific topic
  unique: number // total number of unique user conversations on that date for the specific topic
  total_messages: number // total number of messages in all conversations on that date for the specific topic
  all_conversations: number // total number of conversations on that date for all topics
}

// Analytics Stats Types
// ------------------------------------------------------------
export type AnalyticsStats = {
  average: number
  total: number
  max: number
  min: number
}

export type ConversationStats = {
  averageConversationLength: number
  maxConversationLength: number
  minConversationLength: number
  totalMessages: number
  totalConversations: number
  totalUniqueConversations: number
}

export type CreateStepStreamParams = CreateStepParams

// User Management
// ------------------------------------------------------------
export type UserRole = 'Admin' | 'SME' | 'User' | 'SWI Admin'

export type MemberStatus = 'active' | 'invited' | 'expired' | 'subscribed' | 'disabled' | 'trial_expired'

export type UserSortBy =
  | 'name'
  | 'email'
  | 'last_login'
  | 'created_at'
  | 'is_sponsor_admin'
  | 'is_swi_admin'
  | 'sme_access'
  | 'is_user_disabled'
  | 'total_credits_used'
  | 'last_activity_updated_at'
  | 'status'

export type GetUsersParams = {
  page: number
  pageSize: number
  sortBy?: UserSortBy
  sortDir?: 'asc' | 'desc'
  filterBy?: string
  role?: UserRole
  isUserDisabled?: boolean
}

export type GetUsersResponseEntry = {
  email: string
  name: string | null
  authenticatorId: string
  userId: string
  isSponsorAdmin: boolean
  isSwiAdmin: boolean
  smeAccess: boolean
  sponsorId: string
  status: MemberStatus
  isUserDisabled: boolean
  isInvitationExpired: boolean
  createdAt: string
  lastLogin: string | null
  lastActivityUpdatedAt: string | null
  topicIds: string[] | null
  totalCreditsUsed: number
  subscriptionActive: boolean
  isTrial: boolean
  trialEnd: string | null
  trialDays: number | null
}

export type GetUsersResponse = {
  members: Array<GetUsersResponseEntry>
  page: number
  pageSize: number
  total: number
}

export type CreateUserParams = {
  user_details: {
    email: string
    name?: string
    password?: string
    sme: boolean
    sponsor_admin: boolean
    is_user_disabled?: boolean // default false
    topic_ids?: string[]
    sms_mfa_enabled?: boolean // default false
    mfa_phone_number?: string // optional if sms_mfa_enabled is false
    existing_sponsor_for_user_confirmed?: boolean // Skip confirmation if user exists on another sponsor
    is_trial?: boolean
    trial_days?: number
  }
  personal_message?: string
}

export type CreateUserResponse = {
  success: boolean
  message: string
}

export type CreateBulkUsersParams = {
  file: File
  is_trial?: boolean
  trial_days?: number
}

export type CreateBulkUsersResponse = {
  jobId: string
  message: string
}

export type BulkUploadJobStatusValue = 'started' | 'in_progress' | 'completed' | 'failed'

export type GetBulkUploadJobStatusParams = {
  jobId: string
}

// `errors` is a capped sample of per-row failures, so `errorCount` is the real
// failure total; `error` is only set on a fatal `failed` job.
export type BulkUploadJobStatus = {
  status: BulkUploadJobStatusValue
  total: number
  processed: number
  errorCount: number
  errors: string[]
  message?: string
  updatedAt?: string
  completedAt?: string
  error?: string
}

export type ChangeUserStatusParams = {
  user_id: string
  email: string
  is_user_disabled: boolean
}

export type ChangeUserStatusResponse = {
  success: boolean
  message: string
}

export type UpdateUserParams = {
  email: string
  user_id: string
  name?: string
  sme: boolean
  is_user_disabled: boolean
  sponsor_admin: boolean
  topic_ids?: string[]
  is_trial?: boolean
  trial_days?: number
}

export type UpdateUserResponse = {
  success: boolean
  message: string
}

export type ReinviteUserParams = {
  user_details: {
    user_id?: string
    email: string
    sme: boolean
    sponsor_admin: boolean
    name?: string
  }
  personal_message?: string
}

export type ReinviteUserResponse = {
  success: boolean
  message: string
}

export type PreviewInviteParams = {
  user_details: {
    email: string
    name?: string
    sme: boolean
    sponsor_admin: boolean
    is_user_disabled: boolean
    sms_mfa_enabled: boolean
    is_trial: boolean
    trial_days?: number
  }
  personal_message?: string
}

export type PreviewInviteResponse = {
  recipientEmails: string[]
  body: string
  subject: string
  sourceEmail: string
}

export type GetSponsorInfoResponse = {
  name: string
  id: string
  disableAutoGenerate: boolean
  active: boolean
  createdAt: string
  setupProgress: SetupProgress
  topicsEnabled: boolean
  disabledDueToBilling?: boolean
  ignoreBilling?: boolean
  cmTopics: Array<{
    id: string
    displayName: string
    key: string
    subtopics: Array<{
      id: string
      displayName: string
      key: string
    }>
    priority: number | null
    notRemovable: boolean
  }>
  brandImage: string | null
  brandTitle: string | null
  brandSubtitle: string | null
  brandColor: string | null
  subscriptionUrl: string | null
  selfRegistrationEnabled: boolean
  /**
   * When true, the channel is in "media mode" which alters CM access (per-CM
   * subscriber gating) and adds a separate guest welcome message in settings.
   * Read-only from the frontend for the foreseeable future.
   */
  isMediaMode: boolean
  /**
   * Welcome message shown to non-subscriber/guest users in media-mode channels.
   * Persisted via /sponsor/update with key `guest_user_message`.
   */
  guestUserMessage: string | null
  /**
   * Whether an unauth guest experience is available. Persisted via
   * /sponsor/update with key `guest_user_access`; defaults off for existing channels.
   */
  guestUserAccess: boolean
  /**
   * Number of messages a guest can send before being prompted to log in.
   * Server-controlled (read-only here). May be absent on older backends.
   */
  guestMessageLimit?: number
}

// Billing
// ------------------------------------------------------------
export type PaymentMethod = {
  type: string
  brand?: string
  last4?: string
  expMonth?: number
  expYear?: number
  verified?: string
}

export type BillingPlan = {
  bundleId: string
  name: string
  description: string
  productId: string
  stripePriceId: string
  stripePriceIdDiscount: string | null
  maxUsers: number
  priceDollars: number
  priceDollarsDiscount: number
  createdBy: string
  createdAt: string
  active: boolean
  isDefault: boolean
}

export type GetBillingInfoResponse = {
  billingInfo: {
    paymentMethod: PaymentMethod | null
    nextBillingDate: string
    estimatedBill: number
    currentSubscribers: number
    freeTrial: boolean // Deprecated
    daysLeftInTrial: number // Deprecated
    freeSeats: number
    planId: string
    plan: BillingPlan
  }
}

export type GetBillingCreditsDataParams = {
  start_date: string
  end_date: string
}

export type GetBillingCreditsDataResponse = {
  credits: Array<{
    activeCredits: number
    uniqueUserCount: number
    date: string
  }>
  averageCredits: number
  totalActiveCredits: number
  uniqueUserCount: number
  averagePerUserCredits: number
}

export type GetSubscribersDataParams = {
  back_period?: number
}

export type SubscriberEntry = {
  sponsorId: string
  userCount: number
  day: string
}

export type GetSubscribersDataResponse = {
  current: boolean
  periodStart: string
  periodEnd: string
  hasNext: boolean
  hasPrevious: boolean
  subscribers: SubscriberEntry[]
  mostSubscribers: number
}

// Update current payment method (goes through stripe payment flow)
export type ChangePaymentMethodResponse = {
  clientSecret: string
}

// Remove payment method
export type RemovePaymentMethodParams = {
  keep_id?: string // If provided, the payment method with this id will be kept. (not something the frontend is currently setup to do)
}

export type RemovePaymentMethodResponse = {
  message: string // "Payment methods cleared successfully"
}

export type ChangeToPaidChannelResponse = {}

// Enable auto refill
export type EnableAutoRefillParams = {
  bundle_id: string
  threshold?: number
}

export type EnableAutoRefillResponse = {
  purchased: boolean
  clientSecret?: string
  message?: string
}

// Disable auto refill
export type DisableAutoRefillParams = {}

export type DisableAutoRefillResponse = {
  message: string
}

// One-time credit purchase (under the hood it just a call to generate_client_secret)
export type PurchaseCreditsParams = {
  bundle_id: string
  auto_refill: boolean // In order to maintain the current state of auto refill
}

export type PurchaseCreditsResponse = {
  purchased: boolean
  clientSecret?: string
  message?: string
}

export type UpdateSponsorSetupProgressParams = {
  id: string
  setup_progress: SetupProgress
}

export type UpdateSponsorSetupProgressResponse = {
  message: string
}

/*
 * Presentation Personalities
 */
export type PresentationPersonality = {
  name: string
  description: string
  presentationPersonalityId: string
  displayName: string
}

export type GetPresentationPersonalitiesResponse = PresentationPersonality[]

/*
 * Presentation Styles
 */
export type PresentationStyle = {
  name: string
  description: string
  presentationStyleId: string
  displayName: string
}

export type GetPresentationStylesResponse = PresentationStyle[]

/*
 * Onboarding
 */
export type CreateCustomerInfoParams = {
  org_name: string
  contact_name: string
  contact_title: string
  contact_email: string
  'frc-captcha-response': string
  'invite-code'?: string
}

export type CreateCustomerInfoResponse = {
  customerInfoUuid: string
}

export type GetChannelPublicInfoResponse = {
  channelId: string
  name: string
  brandTitle: string | null
  brandSubtitle: string | null
  brandImage: string | null
  brandColor: string | null
  selfRegistrationEnabled: boolean
  active: boolean
  guestUserAccess: boolean
}

export type GetSelfRegistrationChannelsResponse = {
  sponsorIds: string[]
}

// Public GET /channel_info/:id. Returns brandImage as a full URL (unlike
// /onboarding/channel/:id), but omits selfRegistrationEnabled/active.
export type GetChannelInfoResponse = {
  id: string
  name: string
  brandTitle: string | null
  brandSubtitle: string | null
  brandImage: string | null
  brandColor: string | null
  isMediaMode: boolean
  subscriptionUrl: string | null
  guestUserAccess: boolean
}

export type RegisterEndUserParams = {
  channel_id: string
  name: string
  email: string
  'frc-captcha-response': string
}

export type RegisterEndUserResponse = {
  success: boolean
  message: string
}

export type VerifyEmailParams = {
  code: string
  customer_info_uuid: string
}

export type VerifyEmailResponse = {
  message: string
  verified: boolean
}

export type ResendVerificationEmailParams = {
  email: string
  customerInfoId: string
}

export type ResendVerificationEmailResponse = {
  message: string
}

export type SendConfirmationEmailParams = {
  customer_info_uuid: string
}

export type SendConfirmationEmailResponse = {
  success: boolean
}

// Plans
// An absent planType is a legacy seat plan. Seat pricing must go through getSeatPlans.
export type PlanType = 'seats' | 'credit_pack'

export type Plan = {
  active: boolean
  bundleId: string
  createdAt: string
  createdBy: string
  description: string
  isDefault: boolean
  maxUsers: number | null
  name: string
  planType: PlanType | null
  priceDollars: number
  priceDollarsDiscount: number
  productId: string | null
  stripePriceId: string | null
  stripePriceIdDiscount: string | null
}

export type GetPlansResponse = {
  plans: Plan[]
}

// Generate Client Secret for Stripe
export type GenerateClientSecretParams = {
  customer_info_uuid: string
  bundle_id: string
  auto_refill: boolean
  threshold?: number
}

export type GenerateClientSecretResponse = {
  billingCustomerId: string
  clientSecret: string
}

// Create Customer
export type CreateCustomerParams = {
  customer_info_uuid: string
}

export type CreateCustomerResponse = {
  message: string
}

// Serves as the input type on any kind of field that is actually an input and not a group
export type FieldType = 'string' | 'enum' | 'date'
export type FactType = 'core' | 'specific'
// This isn't named well in the API, it's more like the fact category and could instead be a group.
// Sponsor facts are merged types from the separate backend fact collections, with a derived fact_order
export type SponsorFactType = 'core' | 'sponsor' | 'group' | 'header'

export interface FactFieldRecord {
  name: string
  extractInstructions: string
  usageInstructions: string
  fieldType: FieldType
  active: boolean
  description: string | null
  createdAt: string // ISO date string
  enums: string[] | null
  listOrder: number | null
  group: string | null // Group ID
  factType: FactType
  disableExtraction: boolean
  showEmptySponsorFactToMember: boolean
  factResponseLength: number | null
  id: string
  createdBy: string
}

export interface SponsorFactFieldRecord {
  sponsorId: string
  groupId: string | null
  factId: string | null // Can link to AllFactFieldRecord.id or sometimes be a name
  headerId: string | null
  name: string
  factType: SponsorFactType
  factOrder: number
  fieldType: FieldType | null
  createdAt: string // ISO date string
  id: string
  active: boolean
  updatedAt: string // ISO date string
}

export type SponsorFactFieldUpdateRecord = {
  fact_order: number
  fact_type: string
  name: string
  field_type: string | null
  header_id: string | null
  active: boolean
}

export interface FactField {
  id: string
  name: string
  fieldType: FieldType | null
  factType: SponsorFactType
  headerId: string | null
  factOrder: number
  enabled: boolean
  options?: string[]
}

/*
 * Feedback/Annotations
 */
export type AnnotationKind = 'thumbs-up' | 'thumbs-down' | 'unknown'

export type FeedbackSortBy = 'date' | 'topic' | 'content_module' | 'tag' | 'feedback_type'

export type FeedbackType = 'thumbs-up' | 'thumbs-down'

export type GetFeedbackParams = {
  page?: number // default 1, must be > 0
  pageSize?: number // default 10, must be > 0 and <= 100
  startDate?: string // ISO datetime string
  endDate?: string // ISO datetime string
  topicIds?: string[]
  contentModuleIds?: string[]
  tags?: string[]
  feedbackType?: FeedbackType
  comments?: string
  sortBy?: FeedbackSortBy
  sortOrder?: 'asc' | 'desc'
}

export type FeedbackAnnotation = {
  annotationKind: FeedbackType
  appVersion: string | null
  contentModuleTitle: string
  conversationId: string
  conversationMessages: Array<{
    createdAt: string
    messageId: string
    role: string
    text: string
    userId: string
  }>
  createdAt: string
  feedbackId: string
  id: string | null
  messageId: string
  messageText: string
  spanId: string | null
  sponsorId: string
  tagIds: Array<{ tagId: string; tagName: string }> | null
  taskId: string // this is the CM id as noted
  userId: string | null
  userFeedback: string
  cmTopics: string[]

  topicId: string // added for easier filtering/sorting
  topicName: string // keep for display
}

export type FeedbackStats = {
  modules: number // Number of modules with feedback
  comments: number // Number of feedback items (comments)
  thumbsUp: number
  thumbsDown: number
  negative: number
  positive: number
  byTag: Record<string, number>
  byTopic: Record<string, number>
  byContentModule: Record<string, number>
}

export type FeedbackTag = {
  id: string
  name: string
}

export type GetFeedbackResponse = {
  results: FeedbackAnnotation[]
  stats: FeedbackStats // aggregated statistics
  page: number
  pageSize: number
  totalPages: number
  total: number
}

export type GetFeedbackTagListParams = {
  // No parameters needed for now, but keeping for future extensibility
}

export type GetFeedbackTagListResponse = FeedbackTag[]

/*
 * Invoices
 */
export type GetInvoicesParams = {
  page?: number
  limit?: number
}

export type Invoice = {
  billingDate: string
  periodStart: string
  periodEnd: string
  sponsorId: string
  planId: string
  userCount: number
  planDollars: number
  totalDollars: number
  invoiceId: string
  stripeIntentId: string
  paymentId: string
  paid: boolean
}

export type GetInvoicesResponse = Invoice[]

/*
 * General Information Content
 */
export type GeneralInfoStatus = 'processing' | 'published' | 'incomplete'

export type GeneralInfoResource = {
  resourceId: string
  type: string
  title?: string
  description?: string
  titleAi?: string
  descriptionAi?: string
  isSourceFile: boolean
  showInApp: boolean
  showInAppSupported?: boolean
  resourceKey?: string
  data?: string
  sourceUrl?: string
  fileName?: string
}

export type GeneralInfoContent = {
  contentId: string
  fileName: string | null
  type: 'sponsor_file' | 'system_file'
  sponsorId: string | null
  showInApp: boolean
  useInRag?: boolean
  status: GeneralInfoStatus
  inProcess: boolean
  content?: string
  contentKey?: string | null
  resources: GeneralInfoResource[]
  createdAt: string
  updatedAt?: string
  aiGenerateTemplates?: Record<string, AIGenerateTemplateEntry> | null
}

export type CreateGeneralInfoParams = {
  show_in_app?: boolean
  is_system_file?: boolean
  s3_file?: string
  file_name?: string
  content?: string
  url?: string
  use_llm_for_scraping?: boolean
  title?: string
  description?: string
  title_sys_prompt_id?: string
  title_msg_template_id?: string
  desc_sys_prompt_id?: string
  desc_msg_template_id?: string
}

export type CreateGeneralInfoResponse = {
  contentId: string
  inProcess: boolean
  status: GeneralInfoStatus
}

export type UpdateGeneralInfoParams = {
  title?: string
  description?: string
  show_in_app?: boolean
  use_in_rag?: boolean
  is_system_file?: boolean
  s3_file?: string
  file_name?: string
  content?: string
  url?: string
  use_llm_for_scraping?: boolean
}

export type UpdateGeneralInfoResponse = {
  message: string
  inProcess: boolean
}

export type ListGeneralInfoParams = {
  include_system?: boolean
}

export type DeleteGeneralInfoResponse = {
  message: string
}
